import React from 'react';
import {
    TextField,
    DatePicker,
    Label,
    MessageBar,
    MessageBarType,
    Dropdown,
    IDropdownOption,
    IconButton,
    Stack,
    useTheme,
    DefaultButton,
    Text,
} from '@fluentui/react';
import { ReleasePipelineSource } from '../../AGSS/models/ReleasePipelineSource';

interface OverrideExtendedFieldsProps {
    pipelineSourceType?: ReleasePipelineSource | null;
    tags?: string[];
    startTime?: string | null;
    endTime?: string | null;
    onPipelineSourceTypeChange: (value: ReleasePipelineSource | null) => void;
    onTagsChange: (tags: string[]) => void;
    onStartTimeChange: (date: string | null) => void;
    onEndTimeChange: (date: string | null) => void;
}

export const OverrideExtendedFields: React.FC<OverrideExtendedFieldsProps> = ({
    pipelineSourceType,
    tags = [],
    startTime,
    endTime,
    onPipelineSourceTypeChange,
    onTagsChange,
    onStartTimeChange,
    onEndTimeChange,
}) => {
    const theme = useTheme();
    const placeholderStyles = {
        field: {
            selectors: {
                '::placeholder': {
                    color: theme.palette.neutralQuaternary,
                    opacity: 0.7,
                    fontStyle: 'italic',
                },
            },
        },
    };
    const [newTagInput, setNewTagInput] = React.useState<string>('');
    const [dateError, setDateError] = React.useState<string | null>(null);

    const pipelineSourceTypeOptions: IDropdownOption[] = [
        { key: 'null', text: 'Not Set' },
        { key: ReleasePipelineSource.Classic, text: 'Classic' },
        { key: ReleasePipelineSource.YAML, text: 'YAML' },
    ];

    const handleAddTag = () => {
        const trimmedTag = newTagInput.trim();
        if (trimmedTag && !tags.includes(trimmedTag)) {
            onTagsChange([...tags, trimmedTag]);
            setNewTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        onTagsChange(tags.filter((tag) => tag !== tagToRemove));
    };

    const handleStartDateChange = (date: Date | null | undefined) => {
        if (!date) {
            onStartTimeChange(null);
            setDateError(null);
            return;
        }

        const dateStr = date.toISOString();

        // Validate start date is before end date if end date is set
        if (endTime) {
            const endDate = new Date(endTime);
            if (date > endDate) {
                setDateError('Start date must be before end date');
                return;
            }
        }

        setDateError(null);
        onStartTimeChange(dateStr);
    };

    const handleEndDateChange = (date: Date | null | undefined) => {
        if (!date) {
            onEndTimeChange(null);
            setDateError(null);
            return;
        }

        const dateStr = date.toISOString();

        // Validate end date is after start date if start date is set
        if (startTime) {
            const startDate = new Date(startTime);
            if (date < startDate) {
                setDateError('End date must be after start date');
                return;
            }
        }

        setDateError(null);
        onEndTimeChange(dateStr);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Pipeline Source Type */}
            <div
                style={{
                    padding: '12px',
                    backgroundColor: theme.palette.white,
                    borderRadius: '4px',
                }}
            >
                <Dropdown
                    label="Pipeline Source Type"
                    selectedKey={
                        pipelineSourceType !== null &&
                        pipelineSourceType !== undefined
                            ? pipelineSourceType
                            : 'null'
                    }
                    options={pipelineSourceTypeOptions}
                    onChange={(_, option) => {
                        if (option) {
                            onPipelineSourceTypeChange(
                                option.key === 'null'
                                    ? null
                                    : (option.key as ReleasePipelineSource)
                            );
                        }
                    }}
                    styles={{ dropdown: { width: '100%' } }}
                />
                <Text
                    variant="small"
                    styles={{
                        root: {
                            color: theme.palette.neutralSecondary,
                            marginTop: '4px',
                            display: 'block',
                        },
                    }}
                >
                    Indicates whether this filter applies to YAML or Classic
                    pipelines
                </Text>
            </div>

            {/* Tags */}
            <div
                style={{
                    padding: '12px',
                    backgroundColor: theme.palette.white,
                    borderRadius: '4px',
                }}
            >
                <Label
                    styles={{ root: { fontWeight: 600, marginBottom: '8px' } }}
                >
                    Tags
                </Label>
                <div style={{ marginBottom: '8px' }}>
                    <Stack
                        horizontal
                        tokens={{ childrenGap: 8 }}
                        verticalAlign="end"
                    >
                        <Stack.Item grow={1}>
                            <TextField
                                placeholder="Add a tag..."
                                value={newTagInput}
                                onChange={(_, newValue) =>
                                    setNewTagInput(newValue || '')
                                }
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddTag();
                                    }
                                }}
                                styles={placeholderStyles}
                            />
                        </Stack.Item>
                        <DefaultButton
                            text="Add"
                            iconProps={{ iconName: 'Add' }}
                            onClick={handleAddTag}
                            disabled={!newTagInput.trim()}
                        />
                    </Stack>
                </div>

                {tags.length > 0 && (
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                            marginTop: '8px',
                        }}
                    >
                        {tags.map((tag, index) => (
                            <div
                                key={index}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '4px 8px',
                                    backgroundColor: theme.palette.themeLighter,
                                    border: `1px solid ${theme.palette.themePrimary}`,
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    color: theme.palette.themeDarker,
                                    fontWeight: 600,
                                }}
                            >
                                <span>{tag}</span>
                                <IconButton
                                    iconProps={{ iconName: 'Cancel' }}
                                    title="Remove tag"
                                    ariaLabel={`Remove tag ${tag}`}
                                    onClick={() => handleRemoveTag(tag)}
                                    styles={{
                                        root: {
                                            width: 16,
                                            height: 16,
                                            padding: 0,
                                        },
                                        icon: {
                                            fontSize: 10,
                                        },
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}

                {tags.length === 0 && (
                    <div
                        style={{
                            padding: '8px',
                            backgroundColor: theme.palette.neutralLighter,
                            borderRadius: '4px',
                            fontStyle: 'italic',
                            fontSize: '12px',
                            color: theme.palette.neutralSecondary,
                        }}
                    >
                        No tags added yet. Tags help categorize and search for
                        overrides.
                    </div>
                )}
            </div>

            {/* Timeframe */}
            <div
                style={{
                    padding: '12px',
                    backgroundColor: theme.palette.white,
                    borderRadius: '4px',
                }}
            >
                <Label
                    styles={{ root: { fontWeight: 600, marginBottom: '8px' } }}
                >
                    Active Timeframe (Optional)
                </Label>
                <div
                    style={{
                        fontSize: '12px',
                        color: theme.palette.neutralSecondary,
                        marginBottom: '12px',
                        padding: '8px',
                        backgroundColor: theme.palette.neutralLighterAlt,
                        borderRadius: '4px',
                    }}
                >
                    If set, this override will only be active within the
                    specified date range. Leave both fields empty for the
                    override to be active indefinitely.
                </div>

                {dateError && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        styles={{ root: { marginBottom: '12px' } }}
                    >
                        {dateError}
                    </MessageBar>
                )}

                <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
                    <Stack.Item grow={1} styles={{ root: { minWidth: 200 } }}>
                        <Stack
                            horizontal
                            tokens={{ childrenGap: 4 }}
                            verticalAlign="end"
                        >
                            <Stack.Item grow={1}>
                                <DatePicker
                                    label="Start Date"
                                    placeholder="Select start date..."
                                    value={
                                        startTime
                                            ? new Date(startTime)
                                            : undefined
                                    }
                                    onSelectDate={handleStartDateChange}
                                    formatDate={(date) =>
                                        date ? date.toLocaleDateString() : ''
                                    }
                                    showGoToToday={true}
                                    allowTextInput={false}
                                />
                            </Stack.Item>
                            {startTime && (
                                <IconButton
                                    iconProps={{ iconName: 'Clear' }}
                                    title="Clear start date"
                                    ariaLabel="Clear start date"
                                    onClick={() => handleStartDateChange(null)}
                                    styles={{ root: { marginBottom: 4 } }}
                                />
                            )}
                        </Stack>
                    </Stack.Item>

                    <Stack.Item grow={1} styles={{ root: { minWidth: 200 } }}>
                        <Stack
                            horizontal
                            tokens={{ childrenGap: 4 }}
                            verticalAlign="end"
                        >
                            <Stack.Item grow={1}>
                                <DatePicker
                                    label="End Date"
                                    placeholder="Select end date..."
                                    value={
                                        endTime ? new Date(endTime) : undefined
                                    }
                                    onSelectDate={handleEndDateChange}
                                    formatDate={(date) =>
                                        date ? date.toLocaleDateString() : ''
                                    }
                                    showGoToToday={true}
                                    allowTextInput={false}
                                    minDate={
                                        startTime
                                            ? new Date(startTime)
                                            : undefined
                                    }
                                />
                            </Stack.Item>
                            {endTime && (
                                <IconButton
                                    iconProps={{ iconName: 'Clear' }}
                                    title="Clear end date"
                                    ariaLabel="Clear end date"
                                    onClick={() => handleEndDateChange(null)}
                                    styles={{ root: { marginBottom: 4 } }}
                                />
                            )}
                        </Stack>
                    </Stack.Item>
                </Stack>

                {(startTime || endTime) && (
                    <div
                        style={{
                            marginTop: '12px',
                            padding: '8px',
                            backgroundColor: theme.palette.neutralLighterAlt,
                            borderRadius: '4px',
                            fontSize: '12px',
                        }}
                    >
                        <strong>Active Period:</strong>{' '}
                        {startTime
                            ? new Date(startTime).toLocaleDateString()
                            : 'Immediately'}
                        {' - '}
                        {endTime
                            ? new Date(endTime).toLocaleDateString()
                            : 'Indefinitely'}
                    </div>
                )}
            </div>
        </div>
    );
};
